/**
 * The icon catalogue behind every icon picker in the panel.
 *
 * Built from `icons.json`, the same list the BFF's consumers render from. Values are
 * `Namespace.Glyph`; the glyph is what the eye reads, so search matches on it first.
 *
 * **It lives in `Shared/` because two modules validate against it, and validating is the point.**
 * What's New picks the icon of an announcement item; Opt-in Features picks the two faces of the
 * sidebar entry point. Neither BFF checks the name against the real catalogue — both check only
 * the `Namespace.Glyph` shape — so the panel is the only place a name that does not exist can be
 * caught. A second copy of this list would go stale on the first `gl-icons` release.
 *
 * @module Shared/icons
 */

import iconsData from './icons.json';

/** One selectable icon. */
export interface CatalogIcon {
  /** Stored value, e.g. `Communication.Megaphone`. */
  value: string;
  namespace: string;
  glyph: string;
}

/**
 * How many results the picker renders.
 *
 * The catalogue is in the hundreds; drawing all of them makes the list unusable and janky, and
 * nobody scrolls past the first screen anyway. Narrowing the search is the intended path.
 */
export const MAX_ICON_RESULTS = 80;

export const ALL_ICONS: CatalogIcon[] = Object.entries(
  iconsData as Record<string, string[]>,
).flatMap(([namespace, glyphs]) =>
  glyphs.map(glyph => ({ value: `${namespace}.${glyph}`, namespace, glyph })),
);

const KNOWN_ICON_VALUES = new Set(ALL_ICONS.map(icon => icon.value));

/**
 * Whether the build actually has this icon.
 *
 * Checked before rendering rather than caught afterwards: `GLIcon` does not throw on an unknown
 * variant, it renders the words "Icon not found: X" into the layout — which is how a pasted
 * `Map.Layers` ended up as a paragraph sitting across the item card.
 */
export function isKnownIcon(value: string): boolean {
  return KNOWN_ICON_VALUES.has(value.trim());
}

/** The glyph half of a stored value; the whole string when there is no namespace. */
export function getIconGlyph(value: string): string {
  const separator = value.indexOf('.');
  return separator === -1 ? value : value.slice(separator + 1);
}

/**
 * Fallback for an icon with no SVG in this build — the glyph's capitals, so `ChartLineUp` reads
 * as "CLU" rather than an empty box.
 */
export function getIconInitials(value: string): string {
  const glyph = getIconGlyph(value);
  const capitals = glyph.match(/[A-Z]/g);

  if (capitals && capitals.length > 0) return capitals.slice(0, 3).join('');
  return glyph.slice(0, 2).toUpperCase();
}

/**
 * Icons matching a query, capped at {@link MAX_ICON_RESULTS}.
 *
 * Matches the glyph and the namespace, case-insensitively. An empty query returns the first page
 * of the catalogue rather than nothing, so the picker opens with something to look at.
 */
export function searchIcons(query: string, limit = MAX_ICON_RESULTS): CatalogIcon[] {
  const term = query.trim().toLowerCase();
  if (term.length === 0) return ALL_ICONS.slice(0, limit);

  return ALL_ICONS.filter(
    icon => icon.glyph.toLowerCase().includes(term) || icon.namespace.toLowerCase().includes(term),
  ).slice(0, limit);
}

/** Total matches, before the cap — the picker's footer reports "X de Y". */
export function countIconMatches(query: string): number {
  const term = query.trim().toLowerCase();
  if (term.length === 0) return ALL_ICONS.length;

  return ALL_ICONS.filter(
    icon => icon.glyph.toLowerCase().includes(term) || icon.namespace.toLowerCase().includes(term),
  ).length;
}
